"use client";

import type React from "react";

import { useState, useEffect } from "react";
import { Input, List, Avatar, Empty } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { userGetBooks } from "../../apiservice/userService";
import { IBookTypeData } from "../../apiservice/userService.type";

const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<IBookTypeData[]>([]);
  const [showResults, setShowResults] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      return;
    }

    searchBook();
  }, [searchTerm]);

  const searchBook = async () => {
    try {
      const bookData = await userGetBooks({ searchString: searchTerm });
      if (bookData) {
        setSearchResults(bookData?.data);
      } else {
        setError("Book not found");
      }
    } catch (err) {
      setError("Failed to load book data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setShowResults(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setShowResults(e.target.value.trim() !== "");
  };

  const handleBlur = () => {
    // Delay hiding results to allow for clicking on a result
    setTimeout(() => {
      setShowResults(false);
    }, 200);
  };

  // if (loading) {
  //   return <div className="p-4 text-center">Loading book details...</div>;
  // }

  // if (error || !searchResults) {
  //   return (
  //     <div className="p-4 text-center text-red-500">
  //       {error || "Book not found"}
  //     </div>
  //   );
  // }

  return (
    <div className="relative w-full mt-2 px-4 py-2">
      <Input
        placeholder="Search books, authors, categories..."
        allowClear
        prefix={<SearchOutlined className="text-gray-400 mr-1" />}
        size="large"
        onPressEnter={(e) => handleSearch(e.currentTarget.value)}
        onChange={handleChange}
        onBlur={handleBlur}
        value={searchTerm}
        className="shadow-sm"
        style={{ borderRadius: 9999, paddingLeft: 16 }}
      />

      {showResults && searchTerm.trim() !== "" && (
        <div className="absolute z-10 left-4 right-4 mt-2 bg-white dark:bg-stone-700 rounded-2xl shadow-xl border border-gray-100 dark:border-stone-600 max-h-96 overflow-y-auto px-2">
          {searchResults.length > 0 ? (
            <List
              itemLayout="horizontal"
              dataSource={searchResults}
              renderItem={(book) => (
                <List.Item>
                  <Link
                    to={`/users/book-details/${book.id}/${encodeURIComponent(
                      book.title.replace(/\s+/g, "_")
                    )}`}
                    className="w-full"
                    onClick={() => setShowResults(false)}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          src={`${process.env.PUBLIC_URL + book.coverImageUrl}`}
                          shape="square"
                          size={64}
                        />
                      }
                      title={
                        <span className="text-gray-900 dark:text-stone-100">
                          {book.title}
                        </span>
                      }
                      description={
                        <div>
                          <div className="text-gray-600 dark:text-stone-400">
                            {book.authorName}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(book.categories || []).map((cat) => (
                              <span
                                key={cat.id}
                                className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-full"
                              >
                                {cat.bookCategory?.title}
                              </span>
                            ))}
                          </div>
                        </div>
                      }
                    />
                  </Link>
                </List.Item>
              )}
            />
          ) : (
            <Empty
              description={
                <span className="text-gray-900 dark:text-stone-100">
                  No books found matching your criteria
                </span>
              }
              className="py-4"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
